import React, { useState, useEffect } from 'react';
import WorkflowProgressIndicator from '../../components/ui/WorkflowProgressIndicator';
import ProcessingStatusBadge from '../../components/ui/ProcessingStatusBadge';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProductComparisonPanel from './components/ProductComparisonPanel';
import SimilarityScoreDisplay from './components/SimilarityScoreDisplay';
import DecisionToolbar from './components/DecisionToolbar';
import ReviewProgressIndicator from './components/ReviewProgressIndicator';
import RelatedProductsPanel from './components/RelatedProductsPanel';
import PricingHistoryPanel from './components/PricingHistoryPanel';
import PreviousDecisionsPanel from './components/PreviousDecisionsPanel';

const ManualReviewInterface = () => {
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [reviewedMatches, setReviewedMatches] = useState(new Set());
  const [decisionHistory, setDecisionHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobileTabView, setIsMobileTabView] = useState(false);
  const [activeTab, setActiveTab] = useState('primary');

  // Mock data for ambiguous matches
  const ambiguousMatches = [
  {
    id: 1,
    primaryProduct: {
      name: "Samsung Galaxy S23 Ultra 256GB Phantom Black",
      brand: "Samsung",
      mpn: "SM-S918BZKGEUB",
      ean: "8806094559347",
      sku: "SAM-S23U-256-BLK",
      description: "Premium flagship smartphone with 200MP camera, S Pen support, and 5000mAh battery. Features 6.8-inch Dynamic AMOLED display with 120Hz refresh rate.",
      price: 1199.99,
      inStock: true,
      quantity: 15,
      image: "https://images.unsplash.com/photo-1678911820864-e2c567c655d7",
      imageAlt: "Black Samsung Galaxy S23 Ultra smartphone showing front display with S Pen stylus",
      supplier: {
        name: "TechDistributor Pro",
        contact: "orders@techdistributor.com",
        lastUpdated: "2024-10-15T10:30:00Z"
      }
    },
    suggestedMatch: {
      name: "Samsung Galaxy S23 Ultra 256GB Black",
      brand: "Samsung",
      mpn: "SM-S918B",
      ean: "8806094559347",
      sku: "SAMS23U256B",
      description: "Samsung Galaxy S23 Ultra with 256GB storage in black color. Includes 200MP main camera, S Pen functionality, and premium build quality.",
      price: 1189.99,
      inStock: true,
      quantity: 8,
      image: "https://images.unsplash.com/photo-1678911820864-e2c567c655d7",
      imageAlt: "Samsung Galaxy S23 Ultra in black color displaying home screen interface",
      supplier: {
        name: "MobileSource Direct",
        contact: "sales@mobilesource.com",
        lastUpdated: "2024-10-16T14:20:00Z"
      }
    },
    similarityScore: 0.89,
    matchingCriteria: [
    { type: 'ean', score: 1.0, isExactMatch: true },
    { type: 'brand', score: 1.0, isExactMatch: true },
    { type: 'mpn', score: 0.85, isExactMatch: false },
    { type: 'name', score: 0.92, isExactMatch: false },
    { type: 'description', score: 0.78, isExactMatch: false }]

  },
  {
    id: 2,
    primaryProduct: {
      name: "Apple MacBook Pro 14-inch M3 Pro 512GB Space Black",
      brand: "Apple",
      mpn: "MRX33LL/A",
      ean: "194253715849",
      sku: "APPLE-MBP14-M3P-512-SB",
      description: "Professional laptop with M3 Pro chip, 14-inch Liquid Retina XDR display, 512GB SSD storage, and advanced thermal design for sustained performance.",
      price: 2399.99,
      inStock: true,
      quantity: 5,
      image: "https://images.unsplash.com/photo-1665660411308-89f6f9645a62",
      imageAlt: "Space Black MacBook Pro 14-inch open on wooden desk showing macOS desktop",
      supplier: {
        name: "Apple Authorized Reseller",
        contact: "business@applereseller.com",
        lastUpdated: "2024-10-16T09:15:00Z"
      }
    },
    suggestedMatch: {
      name: "MacBook Pro 14\" M3 Pro Chip 512GB - Space Black",
      brand: "Apple",
      mpn: "MRX33",
      ean: "194253715849",
      sku: "MBP14M3P512SB",
      description: "Apple MacBook Pro featuring M3 Pro processor, 14-inch display with ProMotion technology, 512GB storage capacity, and premium space black finish.",
      price: 2349.99,
      inStock: false,
      quantity: 0,
      image: "https://images.unsplash.com/photo-1659978206090-234e8dc06601",
      imageAlt: "Apple MacBook Pro in space black color with lid closed on white background",
      supplier: {
        name: "Computer Warehouse Plus",
        contact: "orders@computerwarehouse.com",
        lastUpdated: "2024-10-14T16:45:00Z"
      }
    },
    similarityScore: 0.91,
    matchingCriteria: [
    { type: 'ean', score: 1.0, isExactMatch: true },
    { type: 'brand', score: 1.0, isExactMatch: true },
    { type: 'mpn', score: 0.88, isExactMatch: false },
    { type: 'name', score: 0.89, isExactMatch: false },
    { type: 'description', score: 0.82, isExactMatch: false }]

  },
  {
    id: 3,
    primaryProduct: {
      name: "Sony WH-1000XM5 Wireless Noise Canceling Headphones Black",
      brand: "Sony",
      mpn: "WH1000XM5/B",
      ean: "4548736134447",
      sku: "SONY-WH1000XM5-BLK",
      description: "Premium wireless headphones with industry-leading noise cancellation, 30-hour battery life, and crystal-clear call quality with precise voice pickup.",
      price: 399.99,
      inStock: true,
      quantity: 12,
      image: "https://images.unsplash.com/photo-1583305727488-61f82c7eae4b",
      imageAlt: "Black Sony WH-1000XM5 wireless headphones on white background showing side profile",
      supplier: {
        name: "Audio Excellence",
        contact: "info@audioexcellence.com",
        lastUpdated: "2024-10-16T11:30:00Z"
      }
    },
    suggestedMatch: {
      name: "Sony WH-1000XM5 Noise Cancelling Headphones - Black",
      brand: "Sony",
      mpn: "WH1000XM5B",
      ean: "4548736134447",
      sku: "WH1000XM5BLK",
      description: "Sony's flagship noise-cancelling headphones with advanced processor, exceptional sound quality, and all-day comfort for professional and personal use.",
      price: 379.99,
      inStock: true,
      quantity: 20,
      image: "https://images.unsplash.com/photo-1584433202630-5dc24e240130",
      imageAlt: "Sony WH-1000XM5 headphones in black color positioned on modern desk setup",
      supplier: {
        name: "Sound Solutions Pro",
        contact: "sales@soundsolutions.com",
        lastUpdated: "2024-10-15T13:20:00Z"
      }
    },
    similarityScore: 0.87,
    matchingCriteria: [
    { type: 'ean', score: 1.0, isExactMatch: true },
    { type: 'brand', score: 1.0, isExactMatch: true },
    { type: 'mpn', score: 0.82, isExactMatch: false },
    { type: 'name', score: 0.88, isExactMatch: false },
    { type: 'description', score: 0.75, isExactMatch: false }]

  }];


  // Mock related products
  const relatedProducts = [
  {
    name: "Samsung Galaxy S23 Ultra 512GB Phantom Black",
    brand: "Samsung",
    price: 1399.99,
    similarity: 0.95,
    image: "https://images.unsplash.com/photo-1611282104838-d5b4586782e7",
    imageAlt: "Samsung Galaxy S23 Ultra 512GB model in phantom black color",
    matchingAttributes: ['Brand', 'Model', 'Color']
  },
  {
    name: "Samsung Galaxy S23+ 256GB Phantom Black",
    brand: "Samsung",
    price: 999.99,
    similarity: 0.82,
    image: "https://images.unsplash.com/photo-1636376138153-ff3eac69f862",
    imageAlt: "Samsung Galaxy S23 Plus smartphone in black showing camera module",
    matchingAttributes: ['Brand', 'Storage', 'Color']
  }];


  // Mock pricing history
  const pricingHistory = [
  { date: "2024-09-15", price: 1249.99 },
  { date: "2024-09-22", price: 1229.99 },
  { date: "2024-09-29", price: 1199.99 },
  { date: "2024-10-06", price: 1189.99 },
  { date: "2024-10-13", price: 1199.99 }];


  // Mock previous decisions
  const previousDecisions = [
  {
    productA: "Samsung Galaxy S23 Ultra 256GB Black",
    productB: "Samsung Galaxy S23 Ultra 256GB Phantom Black",
    decision: 'merged',
    similarity: 0.92,
    matchingCriteria: ['EAN', 'Brand', 'Model'],
    timestamp: "2024-10-10T14:30:00Z",
    confidence: 0.95
  },
  {
    productA: "Apple MacBook Pro 14-inch M3",
    productB: "MacBook Pro 14\" M3 Chip",
    decision: 'merged',
    similarity: 0.89,
    matchingCriteria: ['Brand', 'Model', 'Screen Size'],
    timestamp: "2024-10-09T16:20:00Z",
    confidence: 0.88
  }];


  const currentMatch = ambiguousMatches?.[currentMatchIndex];
  const totalMatches = ambiguousMatches?.length;
  const completedReviews = reviewedMatches?.size;

  // Check for mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileTabView(window.innerWidth < 1024);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const handleDecision = (decision, matchId = currentMatch?.id) => {
    if (!matchId || isProcessing) return;

    setIsProcessing(true);

    // Simulate processing delay
    setTimeout(() => {
      const newDecision = {
        matchId,
        decision,
        timestamp: new Date()?.toISOString(),
        similarity: currentMatch?.similarityScore || 0
      };

      setDecisionHistory((prev) => [...prev, newDecision]);
      setReviewedMatches((prev) => new Set([...prev, matchId]));

      // Auto-advance to next match if not the last one
      if (currentMatchIndex < totalMatches - 1) {
        setCurrentMatchIndex((prev) => prev + 1);
      }

      setIsProcessing(false);
    }, 1000);
  };

  const handleUndo = () => {
    if (decisionHistory?.length === 0) return;

    const lastDecision = decisionHistory?.[decisionHistory?.length - 1];
    setDecisionHistory((prev) => prev?.slice(0, -1));
    setReviewedMatches((prev) => {
      const newSet = new Set(prev);
      newSet?.delete(lastDecision?.matchId);
      return newSet;
    });
  };

  const handleNavigate = (newIndex) => {
    if (newIndex >= 0 && newIndex < totalMatches) {
      setCurrentMatchIndex(newIndex);
    }
  };

  const handleProductSelect = (product) => {
    console.log('Selected related product:', product);
  };

  const handleApplyDecision = (decision) => {
    handleDecision(decision?.decision);
  };

  const handleSaveAndContinue = () => {
    // Navigate to results dashboard
    window.location.href = '/results-dashboard';
  };

  const handleBulkActions = () => {
    // Implement bulk actions modal
    console.log('Opening bulk actions');
  };

  if (!currentMatch) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-16">
          <div className="container mx-auto px-6 py-8">
            <div className="text-center py-16">
              <Icon name="CheckCircle" size={64} className="text-success mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                All Reviews Complete!
              </h2>
              <p className="text-muted-foreground mb-6">
                You have successfully reviewed all ambiguous matches.
              </p>
              <Button
                variant="default"
                onClick={handleSaveAndContinue}
                iconName="ArrowRight"
                iconPosition="right">
                View Results Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-16">
        <div className="container mx-auto px-6 py-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                Manual Review Interface
              </h1>
              <p className="text-muted-foreground">
                Review ambiguous product matches and make decisions to improve matching accuracy
              </p>
            </div>
            
            <div className="flex items-center space-x-4 mt-4 lg:mt-0">
              <ProcessingStatusBadge
                status={isProcessing ? 'processing' : 'idle'}
                message="Processing decision" />

              <Button
                variant="outline"
                onClick={handleBulkActions}
                iconName="Layers"
                iconPosition="left"
                size="sm">
                Bulk Actions
              </Button>
              <Button
                variant="default"
                onClick={handleSaveAndContinue}
                iconName="Save"
                iconPosition="left"
                size="sm">
                Save & Continue
              </Button>
            </div>
          </div>

          {/* Workflow Progress */}
          <div className="mb-6">
            <WorkflowProgressIndicator
              currentStep={4}
              completedSteps={['upload', 'configure', 'process', 'analyze']} />

          </div>

          {/* Mobile Tab Navigation */}
          {isMobileTabView &&
          <div className="mb-6">
              <div className="flex border-b border-border">
                <button
                onClick={() => setActiveTab('primary')}
                className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'primary' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                }>

                  Primary Product
                </button>
                <button
                onClick={() => setActiveTab('suggested')}
                className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'suggested' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                }>

                  Suggested Match
                </button>
                <button
                onClick={() => setActiveTab('similarity')}
                className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'similarity' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                }>

                  Similarity
                </button>
              </div>
            </div>
          }

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column - Product Comparison */}
            <div className="lg:col-span-8">
              {isMobileTabView ?
              // Mobile Tab View
              <div className="space-y-6">
                  {activeTab === 'primary' &&
                <ProductComparisonPanel
                  product={currentMatch?.primaryProduct}
                  title="Primary Product"
                  isPrimary={true} />

                }
                  {activeTab === 'suggested' &&
                <ProductComparisonPanel
                  product={currentMatch?.suggestedMatch}
                  title="Suggested Match" />

                }
                  {activeTab === 'similarity' &&
                <SimilarityScoreDisplay
                  overallScore={currentMatch?.similarityScore}
                  matchingCriteria={currentMatch?.matchingCriteria} />

                }
                </div> :

              // Desktop Split View
              <div className="space-y-6">
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <ProductComparisonPanel
                    product={currentMatch?.primaryProduct}
                    title="Primary Product"
                    isPrimary={true} />

                    <ProductComparisonPanel
                    product={currentMatch?.suggestedMatch}
                    title="Suggested Match" />

                  </div>
                  <SimilarityScoreDisplay
                  overallScore={currentMatch?.similarityScore}
                  matchingCriteria={currentMatch?.matchingCriteria} />

                </div>
              }

              {/* Decision Toolbar */}
              <div className="mt-6">
                <DecisionToolbar
                  onMerge={() => handleDecision('merged')}
                  onKeepSeparate={() => handleDecision('separate')}
                  onSkipForLater={() => handleDecision('skipped')}
                  onUndo={handleUndo}
                  canUndo={decisionHistory?.length > 0}
                  isProcessing={isProcessing} />

              </div>
            </div>

            {/* Right Column - Context Panels */}
            <div className="lg:col-span-4 space-y-6">
              <ReviewProgressIndicator
                currentIndex={currentMatchIndex}
                totalMatches={totalMatches}
                completedReviews={completedReviews}
                onNavigate={handleNavigate} />


              <RelatedProductsPanel
                relatedProducts={relatedProducts}
                onProductSelect={handleProductSelect} />


              <PricingHistoryPanel
                priceHistory={pricingHistory}
                currentPrice={currentMatch?.primaryProduct?.price}
                productName={currentMatch?.primaryProduct?.name} />


              <PreviousDecisionsPanel
                previousDecisions={previousDecisions}
                onApplyDecision={handleApplyDecision} />

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualReviewInterface;